import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  flashcardDecksService,
  type FlashcardDeckDto,
  type FlashcardDeckCreateDto,
  type FlashcardDeckUpdateDto,
} from '../api/flashcardDecksService';
import {
  flashcardsService,
  type FlashcardDto,
  type FlashcardCreateDto,
  type FlashcardUpdateDto,
} from '../api/flashcardsService';

/**
 * Hook to manage flashcard decks for a specific book
 */
export function useFlashcardDecks(bookId: string) {
  const queryClient = useQueryClient();

  // Fetch all decks for this book
  const {
    data: decks = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['flashcard-decks', bookId],
    queryFn: () => flashcardDecksService.getMyDecks(bookId),
    enabled: !!bookId,
  });

  // Create deck mutation
  const createDeckMutation = useMutation({
    mutationFn: (input: FlashcardDeckCreateDto) =>
      flashcardDecksService.createMyDeck(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flashcard-decks', bookId] });
    },
  });

  // Update deck mutation
  const updateDeckMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: FlashcardDeckUpdateDto }) =>
      flashcardDecksService.updateMyDeck(id, bookId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flashcard-decks', bookId] });
    },
  });

  // Delete deck mutation
  const deleteDeckMutation = useMutation({
    mutationFn: (id: string) => flashcardDecksService.deleteMyDeck(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flashcard-decks', bookId] });
    },
  });

  return {
    decks,
    isLoading,
    error,
    createDeck: createDeckMutation.mutateAsync,
    updateDeck: updateDeckMutation.mutateAsync,
    deleteDeck: deleteDeckMutation.mutateAsync,
    isCreating: createDeckMutation.isPending,
    isUpdating: updateDeckMutation.isPending,
    isDeleting: deleteDeckMutation.isPending,
  };
}

/**
 * Hook to manage flashcards within a specific deck
 */
export function useFlashcardsInDeck(deckId: string | null) {
  const queryClient = useQueryClient();

  // Fetch all flashcards in this deck
  const {
    data: flashcards = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['flashcards', deckId],
    queryFn: () => flashcardsService.getMyFlashcards(deckId!),
    enabled: !!deckId,
  });

  // Create flashcard mutation
  const createFlashcardMutation = useMutation({
    mutationFn: (input: FlashcardCreateDto) =>
      flashcardsService.createMyFlashcard(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flashcards', deckId] });
      queryClient.invalidateQueries({ queryKey: ['flashcard-decks'] });
    },
  });

  // Update flashcard mutation
  const updateFlashcardMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: FlashcardUpdateDto }) =>
      flashcardsService.updateMyFlashcard(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flashcards', deckId] });
    },
  });

  // Delete flashcard mutation
  const deleteFlashcardMutation = useMutation({
    mutationFn: (id: string) => flashcardsService.deleteMyFlashcard(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flashcards', deckId] });
      queryClient.invalidateQueries({ queryKey: ['flashcard-decks'] });
    },
  });

  // Record study result mutation
  const recordStudyResultMutation = useMutation({
    mutationFn: ({ id, isCorrect }: { id: string; isCorrect: boolean }) =>
      flashcardsService.recordStudyResult(id, isCorrect),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flashcards', deckId] });
    },
  });

  return {
    flashcards,
    isLoading,
    error,
    createFlashcard: createFlashcardMutation.mutateAsync,
    updateFlashcard: updateFlashcardMutation.mutateAsync,
    deleteFlashcard: deleteFlashcardMutation.mutateAsync,
    recordStudyResult: recordStudyResultMutation.mutateAsync,
    isCreating: createFlashcardMutation.isPending,
    isUpdating: updateFlashcardMutation.isPending,
    isDeleting: deleteFlashcardMutation.isPending,
    isRecording: recordStudyResultMutation.isPending,
  };
}
